import { useCallback, useEffect, useState } from "react";
import { RATE_LIMIT_COOLDOWN_MS } from "@/constants/spinningWheel";

/**
 * Hook for managing rate limiting on wheel spins
 */
export const useWheelRateLimit = () => {
  const [lastSpinTime, setLastSpinTime] = useState<number | null>(null);
  const [clientSideDelay, setClientSideDelay] = useState<number>(0);

  /**
   * Check if a spin can be executed based on rate limits
   */
  const canSpin = useCallback((): { allowed: boolean; remainingSeconds?: number; message?: string } => {
    if (clientSideDelay > 0) {
      const message = `Please wait ${clientSideDelay} second${clientSideDelay !== 1 ? "s" : ""} before spinning again`;
      return { allowed: false, remainingSeconds: clientSideDelay, message };
    }

    const now = Date.now();
    if (lastSpinTime && now - lastSpinTime < RATE_LIMIT_COOLDOWN_MS) {
      const remainingMs = RATE_LIMIT_COOLDOWN_MS - (now - lastSpinTime);
      const remainingSeconds = Math.ceil(remainingMs / 1000);
      const message = `Please wait ${remainingSeconds} second${
        remainingSeconds !== 1 ? "s" : ""
      } before spinning again`;
      return { allowed: false, remainingSeconds, message };
    }

    return { allowed: true };
  }, [lastSpinTime, clientSideDelay]);

  /**
   * Register a new spin attempt
   */
  const registerSpin = useCallback(() => {
    const now = Date.now();
    setLastSpinTime(now);
    setClientSideDelay(RATE_LIMIT_COOLDOWN_MS / 1000);
  }, []);

  // Client-side countdown
  useEffect(() => {
    if (clientSideDelay > 0) {
      const interval = setInterval(() => {
        setClientSideDelay((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
    return undefined;
  }, [clientSideDelay]);

  return {
    canSpin,
    registerSpin,
    clientSideDelay,
    isRateLimited: clientSideDelay > 0,
  };
};
